import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV_LINKS } from "../lib/constants";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-[#E5E7EB] shadow-sm"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-home-link">
          <img
            src={COMPANY.logo}
            alt="Impresa Edile Lici"
            className="h-16 w-16 object-contain"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-lg text-[#0F5132] font-semibold">
              Impresa Edile Lici
            </span>
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#4B5563]">
              Fotovoltaico • Edilizia
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-full transition-colors duration-200 ${
                  isActive
                    ? "text-[#0F5132] bg-[#0F5132]/8 font-medium"
                    : "text-[#111827] hover:text-[#0F5132] hover:bg-black/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={COMPANY.phoneHref}
            data-testid="header-phone-cta"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F5132] text-white text-sm font-medium hover:bg-[#0A3622] transition-colors"
          >
            <Phone className="h-4 w-4" />
            {COMPANY.phone}
          </a>
          <button
            type="button"
            onClick={() => navigate("/contatti")}
            data-testid="header-cta-preventivo"
            className="hidden sm:inline-flex items-center px-5 py-2 rounded-full bg-[#D97706] text-white text-sm font-medium hover:bg-[#B45309] transition-colors shadow-sm"
          >
            Preventivo Gratuito
          </button>
          <button
            type="button"
            aria-label="Apri menu"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#E5E7EB] text-[#0F5132]"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[#E5E7EB] bg-white" data-testid="mobile-nav">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 text-base rounded-lg ${
                    isActive
                      ? "text-[#0F5132] bg-[#0F5132]/10 font-medium"
                      : "text-[#111827] hover:bg-black/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={COMPANY.phoneHref}
              data-testid="mobile-phone-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0F5132] text-white font-medium"
            >
              <Phone className="h-4 w-4" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
